import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import COLORS from '@mlb/react-patterns/style/universal.color';
import FONTS from '@mlb/react-patterns/style/fonts.ts';
import IconPattern from '@mlb/react-patterns/components/Icon/icon.tsx';
import Button from '@mlb/react-patterns/components/Button/button.tsx';

const Step = styled.div`
    margin-bottom: 10px;

    .SM & {
        margin-bottom: 0px;
    }
`;

const Container = styled.div`
    display: -ms-grid;
    display: grid;
    grid-template-areas:
        'header'
        'body';
    -ms-grid-columns: 1fr;
    grid-template-columns: 1fr;
    font-family: ${FONTS.primaryFont};

    .XL & {
        grid-template-areas: 'header body';
        -ms-grid-columns: auto auto;
        grid-template-columns: auto auto;
    }
`;

const Header = styled.div`
    -ms-grid-row: 1;
    -ms-grid-column: 1;
    grid-area: header;
    padding: 10px 0px;
    background-color: ${COLORS.mlbGrey6};
    text-align: center;

    .MD & {
        padding: 30px 24px;
    }
`;

const HeaderTitle = styled.h2`
    margin-bottom: 2px;
    line-height: 1;
    font-family: ${FONTS.primaryFont};
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.mlbWhite};

    .SM & {
        margin-bottom: 0px;
        font-size: 30px;
        font-weight: bold;
    }

    .MD & {
        margin-bottom: 15px;
        font-size: 44px;
    }

    .XL & {
        font-size: 32px;
    }
`;

const HeaderCaption = styled.h3`
    margin: 0px;
    line-height: 1;
    font-family: ${FONTS.primaryFont};
    font-size: 24px;
    font-weight: 600;
    color: ${COLORS.mlbWhite};

    .SM & {
        display: none;
    }

    .MD & {
        display: block;
        font-size: 21px;
    }
`;

const Body = styled.div`
    display: -ms-grid;
    -ms-grid-row: 2;
    -ms-grid-column: 1;
    display: grid;
    grid-area: body;
    grid-template-areas:
        'step1'
        'step2'
        'step3'
        ${({ showGetStarted }) => showGetStarted ? "'getstarted'" : undefined};
    -ms-grid-columns: 1fr;
    grid-template-columns: 1fr;
    padding: 12px 15px;
    background-color: ${COLORS.mlbGrey7};

    .SM & {
        grid-template-areas:
            'step1 step2 step3'
            ${({ showGetStarted }) => showGetStarted ? "'getstarted getstarted getstarted'" : undefined};
        -ms-grid-columns: 1fr 1fr 1fr;
        grid-template-columns: 1fr 1fr 1fr;
        grid-column-gap: 32px;
        padding: 12px 30px;
    }

    .MD & {
        padding: 30px;
    }

    .LG & {
        grid-column-gap: 90px;
    }

    .XL & {
        -ms-grid-row: 1;
        -ms-grid-column: 2;
        grid-template-areas: 'step1 step2 step3';
    }
`;

const Icon = styled(IconPattern)`
    margin-right: 15px;
    vertical-align: middle;
    stroke: none;

    .SM & {
        width: 21px;
        height: 21px;
        margin-right: 16px;
    }

    .MD & {
        width: 30px;
        height: 30px;
        margin-right: 12px;
    }
`;

const BodyTitle = styled.span`
    margin-right: 4px;
    color: ${COLORS.mlbWhite};
    font-size: 16px;
    font-weight: bold;
    vertical-align: middle;

    .SM & {
        margin-right: 0px;
        font-size: 21px;
        font-weight: 600;
    }

    .MD & {
        font-size: 26px;
    }
`;

const BodyDescription = styled.p`
    display: inline;
    margin-top: 0px;
    margin-bottom: 0px;
    font-size: 16px;
    line-height: 1.3;
    color: ${COLORS.mlbWhite};
    vertical-align: middle;

    .SM & {
        display: block;
        margin-top: 8px;
        font-size: 14px;
        color: #B0B3B8;
    }

    .MD & {
        font-size: 18px;
    }
`;

const Step1 = styled(Step)`
    -ms-grid-row: 1;
    -ms-grid-column: 1;
    grid-area: step1;

    .SM & {
        -ms-grid-row: 1;
        -ms-grid-column: 1;
    }
`;

const Step2 = styled(Step)`
    -ms-grid-row: 2;
    -ms-grid-column: 1;
    grid-area: step2;

    .SM & {
        -ms-grid-row: 1;
        -ms-grid-column: 2;
        ${Icon} {
            width: 25px;
            height: 25px;
        }
    }

    .MD & {
        ${Icon} {
            width: 36px;
            height: 36px;
        }
    }
`;

const Step3 = styled(Step)`
    -ms-grid-row: 3;
    -ms-grid-column: 1;
    grid-area: step3;
    margin-bottom: 0px;

    .SM & {
        -ms-grid-row: 1;
        -ms-grid-column: 3;
    }
`;

const GetStarted = styled.div`
    -ms-grid-row: 4;
    grid-area: getstarted;
    margin-top: 23px;
    text-align: center;

    .SM & {
        -ms-grid-row: 2;
    }

    .MD & {
        margin-top: 30px;
    }

    .XL & {
        -ms-grid-row: none;
        grid-area: none;
        margin-top: 0px;
        text-align: left;
    }
`;

const GetStartedButton = styled(Button)`
    &&& {
        width: 100%;

        .SM & {
            width: 250px;
        }
    }
`;

const Content = ({
    config,
    matches,
    lang,
    onGetStartedClick,
}) => {

    const configDictionary = config.dictionary ?? {};
    const dictionary = configDictionary[lang] ?? {};
    const step1Title = dictionary.step1_title ?? 'Step 1';
    const step2Title = dictionary.step2_title ?? 'Step 2';
    const step3Title = dictionary.step3_title ?? 'Step 3';
    const showGetStarted = config.showGetStarted ?? false;
    const getStarted = showGetStarted && (
        <GetStarted>
            <GetStartedButton
                label={dictionary.get_started ?? 'Get Started'}
                size={matches.LG ? 'lg' : 'md'}
                styleName={'link-blue'}
                handlers={{
                    onClick: onGetStartedClick,
                }}
            />
        </GetStarted>
    );

    return (
        <Container>
            <Header>
                <HeaderTitle>{dictionary[matches.SM ? 'header_title' : 'header_short_title'] ?? 'Introducing'}</HeaderTitle>
                {
                    !matches.XL
                        ? <HeaderCaption>{dictionary[matches.SM ? 'header_caption' : 'header_short_caption'] ?? 'Free Text Search'}</HeaderCaption>
                        : getStarted
                }
            </Header>
            <Body showGetStarted={showGetStarted}>
                <Step1>
                    <Icon
                        name={config.step1Icon ?? 'search'}
                        width={16}
                        fill={COLORS.mlbWhite}
                        description={step1Title}
                    />
                    <BodyTitle>{step1Title}</BodyTitle>
                    <BodyDescription>{dictionary[matches.SM ? 'step1_description' : 'step1_short_description'] ?? 'search for your favorite moment.'}</BodyDescription>
                </Step1>
                <Step2>
                    <Icon
                        name={config.step2Icon ?? 'filter'}
                        width={16}
                        fill={COLORS.mlbWhite}
                        description={step2Title}
                    />
                    <BodyTitle>{step2Title}</BodyTitle>
                    <BodyDescription>{dictionary[matches.SM ? 'step2_description' : 'step2_short_description'] ?? 'filter using 35+ video filters.'}</BodyDescription>
                </Step2>
                <Step3>
                    <Icon
                        name={config.step3Icon ?? 'share'}
                        width={16}
                        fill={COLORS.mlbWhite}
                        description={step3Title}
                    />
                    <BodyTitle>{step3Title}</BodyTitle>
                    <BodyDescription>{dictionary[matches.SM ? 'step3_description' : 'step3_short_description'] ?? 'or embed videos.'}</BodyDescription>
                </Step3>
                {
                    !matches.XL && getStarted
                }
            </Body>
        </Container>
    );

};

Content.propTypes = {
    config: PropTypes.object.isRequired,
    matches: PropTypes.shape({
        XS: PropTypes.bool.isRequired,
        SM: PropTypes.bool.isRequired,
        MD: PropTypes.bool.isRequired,
        LG: PropTypes.bool.isRequired,
        XL: PropTypes.bool.isRequired,
    }).isRequired,
    lang: PropTypes.string,
    onGetStartedClick: PropTypes.func,
};

Content.defaultProps = {
    lang: 'en',
    onGetStartedClick: () => {},
};

export default Content;
